// src/pages/GuestList.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Spinner from "react-js-loader";

const API = "https://mocaub1h8d.execute-api.us-east-1.amazonaws.com/prod";

const STATUS_COLORS = {
  PENDIENTE:  { bg: "#fff3cd", color: "#856404" },
  APROBADO:   { bg: "#d1e7dd", color: "#0a5234" },
  RECHAZADO:  { bg: "#f8d7da", color: "#842029" },
};

const GENDER_LABEL = { M: "Masc.", F: "Fem.", Otro: "Otro" };

export default function GuestList() {
  const [guests, setGuests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [selectedEvento, setSelectedEvento] = useState("");
  const [confirmPopup, setConfirmPopup] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });

  const [filters, setFilters] = useState({
    first_name: "",
    last_name: "",
    dni: "",
    email: "",
    gender: "",
    status: "",
  });

  // ── Cargar eventos ──────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.post(`${API}/getEventsSensoria`, { loginType: "1" });
        let lista = [];
        if (Array.isArray(res.data)) lista = res.data;
        else if (res.data?.body) lista = JSON.parse(res.data.body);
        lista.sort((a, b) => new Date(b.fechaEvento) - new Date(a.fechaEvento));
        setEventos(lista);
      } catch {}
    };
    load();
  }, []);

  // ── Cargar inscripciones ────────────────────────────────────────────
  const loadGuests = useCallback(async (eventoId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/getInscripciones`, { idEvento: eventoId });
      let data = [];
      if (Array.isArray(res.data)) data = res.data;
      else if (res.data?.body) data = JSON.parse(res.data.body);
      setGuests(data);
      setFiltered(data);
    } catch {
      setGuests([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEvento) loadGuests(selectedEvento);
    else { setGuests([]); setFiltered([]); }
  }, [selectedEvento, loadGuests]);

  // ── Filtros ─────────────────────────────────────────────────────────
  useEffect(() => {
    let list = [...guests];
    Object.entries(filters).forEach(([k, v]) => {
      if (!v) return;
      list = list.filter((g) =>
        String(g[k] || "").toLowerCase().includes(v.toLowerCase())
      );
    });
    if (sortConfig.key) {
      list.sort((a, b) => {
        const va = String(a[sortConfig.key] || "").toLowerCase();
        const vb = String(b[sortConfig.key] || "").toLowerCase();
        return sortConfig.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    setFiltered(list);
  }, [filters, guests, sortConfig]);

  const setFilter = (k) => (e) =>
    setFilters((p) => ({ ...p, [k]: e.target.value }));

  const toggleSort = (key) =>
    setSortConfig((p) => ({ key, dir: p.key === key && p.dir === "asc" ? "desc" : "asc" }));

  const sortArrow = (key) =>
    sortConfig.key === key ? (sortConfig.dir === "asc" ? " ▲" : " ▼") : "";

  // ── Aprobar / Rechazar ──────────────────────────────────────────────
  const handleAction = async (guest, newStatus) => {
    setConfirmPopup(null);
    setActionLoading(guest.inscripcionId);
    try {
      await axios.post(`${API}/approveInscripcion`, {
        inscripcionId: guest.inscripcionId,
        idEvento: guest.idEvento,
        status: newStatus,
      });
      setGuests((prev) =>
        prev.map((g) =>
          g.inscripcionId === guest.inscripcionId ? { ...g, status: newStatus } : g
        )
      );
    } catch {
      alert("Error al actualizar. Intentá de nuevo.");
    } finally {
      setActionLoading(null);
    }
  };

  const calcAge = (birth_date) => {
    if (!birth_date) return "-";
    const diff = Date.now() - new Date(birth_date).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      {loading && (
        <div style={styles.loaderOverlay}>
          <Spinner type="bubble-top" bgColor="#dfa66b" color="#dfa66b" size={90} />
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Lista de invitados</h2>
        <select
          style={styles.eventoSelect}
          value={selectedEvento}
          onChange={(e) => setSelectedEvento(e.target.value)}
        >
          <option value="">Seleccioná un evento...</option>
          {eventos.map((ev) => (
            <option key={ev.idEvento} value={ev.idEvento}>
              {ev.nombre}
            </option>
          ))}
        </select>
      </div>

      {/* Stats rápidas */}
      {guests.length > 0 && (
        <div style={styles.statsRow}>
          {[
            { label: "Total", val: guests.length, bg: "#e9ecef" },
            { label: "Pendientes", val: guests.filter((g) => g.status === "PENDIENTE").length, bg: "#fff3cd" },
            { label: "Aprobados", val: guests.filter((g) => g.status === "APROBADO").length, bg: "#d1e7dd" },
            { label: "Rechazados", val: guests.filter((g) => g.status === "RECHAZADO").length, bg: "#f8d7da" },
          ].map((s) => (
            <div key={s.label} style={{ ...styles.statChip, background: s.bg }}>
              <span style={{ fontWeight: "700", fontSize: 18 }}>{s.val}</span>
              <span style={{ fontSize: 11, color: "#555" }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      {selectedEvento && (
        <div style={styles.filtersRow}>
          <input style={styles.filterInput} placeholder="Nombre" value={filters.first_name} onChange={setFilter("first_name")} />
          <input style={styles.filterInput} placeholder="Apellido" value={filters.last_name} onChange={setFilter("last_name")} />
          <input style={styles.filterInput} placeholder="DNI" value={filters.dni} onChange={setFilter("dni")} />
          <input style={styles.filterInput} placeholder="Email" value={filters.email} onChange={setFilter("email")} />
          <select style={styles.filterInput} value={filters.gender} onChange={setFilter("gender")}>
            <option value="">Sexo</option>
            <option value="M">Masc.</option>
            <option value="F">Fem.</option>
            <option value="Otro">Otro</option>
          </select>
          <select style={styles.filterInput} value={filters.status} onChange={setFilter("status")}>
            <option value="">Estado</option>
            <option value="PENDIENTE">Pendiente</option>
            <option value="APROBADO">Aprobado</option>
            <option value="RECHAZADO">Rechazado</option>
          </select>
          <button
            style={styles.clearBtn}
            onClick={() => setFilters({ first_name: "", last_name: "", dni: "", email: "", gender: "", status: "" })}
          >
            Limpiar
          </button>
        </div>
      )}

      {/* Tabla */}
      {selectedEvento && !loading && (
        <div style={styles.tableWrap}>
          {filtered.length === 0 ? (
            <p style={{ textAlign: "center", color: "#888", padding: 40 }}>
              {guests.length === 0 ? "No hay inscriptos para este evento." : "Sin resultados para los filtros aplicados."}
            </p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr style={{ background: "#1a1a1a", color: "#dfa66b" }}>
                  {[
                    ["first_name", "Nombre"],
                    ["last_name", "Apellido"],
                    ["dni", "DNI"],
                    ["email", "Email"],
                    ["whatsapp", "WhatsApp"],
                    ["gender", "Sexo"],
                    ["birth_date", "Edad"],
                    ["status", "Estado"],
                  ].map(([key, label]) => (
                    <th
                      key={key}
                      style={styles.th}
                      onClick={() => toggleSort(key)}
                    >
                      {label}{sortArrow(key)}
                    </th>
                  ))}
                  <th style={styles.th}>Comprobante</th>
                  <th style={styles.th}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((g, i) => {
                  const sc = STATUS_COLORS[g.status] || STATUS_COLORS.PENDIENTE;
                  const isActing = actionLoading === g.inscripcionId;
                  return (
                    <tr key={g.inscripcionId || i} style={{ background: i % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                      <td style={styles.td}>{g.first_name}</td>
                      <td style={styles.td}>{g.last_name}</td>
                      <td style={styles.td}>{g.dni}</td>
                      <td style={styles.td}>{g.email}</td>
                      <td style={styles.td}>{g.whatsapp}</td>
                      <td style={styles.td}>{GENDER_LABEL[g.gender] || g.gender}</td>
                      <td style={styles.td}>{calcAge(g.birth_date)} años</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: sc.bg, color: sc.color }}>
                          {g.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {g.payment_proof_url ? (
                          <a href={g.payment_proof_url} target="_blank" rel="noreferrer" style={styles.link}>
                            Ver
                          </a>
                        ) : (
                          <span style={{ color: "#aaa", fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        {isActing ? (
                          <span style={{ fontSize: 12, color: "#888" }}>...</span>
                        ) : (
                          <div style={{ display: "flex", gap: 6 }}>
                            {g.status !== "APROBADO" && (
                              <button
                                style={styles.approveBtn}
                                onClick={() => setConfirmPopup({ guest: g, action: "APROBADO" })}
                              >
                                ✓ Aprobar
                              </button>
                            )}
                            {g.status !== "RECHAZADO" && (
                              <button
                                style={styles.rejectBtn}
                                onClick={() => setConfirmPopup({ guest: g, action: "RECHAZADO" })}
                              >
                                ✗ Rech.
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Popup confirmación */}
      {confirmPopup && (
        <div style={styles.overlay}>
          <div style={styles.popup}>
            <h3 style={{ marginBottom: 10 }}>
              {confirmPopup.action === "APROBADO" ? "✅ Aprobar invitado" : "❌ Rechazar invitado"}
            </h3>
            <p style={{ marginBottom: 6, fontSize: 14 }}>
              <strong>{confirmPopup.guest.first_name} {confirmPopup.guest.last_name}</strong>
            </p>
            <p style={{ fontSize: 13, color: "#555", marginBottom: 20 }}>
              {confirmPopup.action === "APROBADO"
                ? "Se enviará el QR por email y WhatsApp."
                : "Se notificará al invitado por email."}
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <button style={styles.cancelBtn} onClick={() => setConfirmPopup(null)}>
                Cancelar
              </button>
              <button
                style={confirmPopup.action === "APROBADO" ? styles.approveBtn : styles.rejectBtn}
                onClick={() => handleAction(confirmPopup.guest, confirmPopup.action)}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ height: 80 }} />
    </div>
  );
}

const styles = {
  page: {
    background: "#f4f4f4",
    minHeight: "100vh",
    padding: "0 0 20px",
  },
  loaderOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  header: {
    background: "#1a1a1a",
    color: "#fff",
    padding: "16px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  eventoSelect: {
    padding: "10px 14px",
    borderRadius: 8,
    border: "none",
    fontSize: 14,
    background: "#333",
    color: "#dfa66b",
  },
  statsRow: {
    display: "flex",
    gap: 10,
    padding: "14px 16px",
    flexWrap: "wrap",
  },
  statChip: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "10px 18px",
    borderRadius: 10,
    minWidth: 70,
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: "0 16px 12px",
    background: "#fff",
    borderBottom: "1px solid #e0e0e0",
  },
  filterInput: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 13,
    minWidth: 100,
    flex: 1,
  },
  clearBtn: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "none",
    background: "#e0e0e0",
    cursor: "pointer",
    fontSize: 13,
  },
  tableWrap: {
    overflowX: "auto",
    padding: "0 16px",
    marginTop: 12,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  th: {
    padding: "12px 10px",
    textAlign: "left",
    fontWeight: "600",
    cursor: "pointer",
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  td: {
    padding: "10px",
    borderBottom: "1px solid #eee",
    whiteSpace: "nowrap",
  },
  badge: {
    padding: "3px 8px",
    borderRadius: 6,
    fontWeight: "700",
    fontSize: 11,
    whiteSpace: "nowrap",
  },
  link: {
    color: "#dfa66b",
    fontWeight: "600",
    textDecoration: "none",
  },
  approveBtn: {
    background: "#198754",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "5px 10px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: "600",
    whiteSpace: "nowrap",
  },
  rejectBtn: {
    background: "#dc3545",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "5px 10px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: "600",
    whiteSpace: "nowrap",
  },
  cancelBtn: {
    background: "#e0e0e0",
    color: "#333",
    border: "none",
    borderRadius: 8,
    padding: "10px 20px",
    cursor: "pointer",
    fontWeight: "600",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9998,
  },
  popup: {
    background: "#fff",
    borderRadius: 16,
    padding: 28,
    width: 300,
    boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
    textAlign: "center",
  },
};
